import { createCache } from '../utils/cache';

export interface Tag {
    id: number;
    name: string;
    created_at: string;
}

interface ApiResponse {
    success: boolean;
    data: Tag[];
    error?: string;
}

interface CachedData {
    data: Tag[];
    etag: string;
    timestamp: number;
}

// Create cache with ETag support and shorter TTL for validation
const tagCache = createCache<CachedData>('tags', 5); // 5 minutes TTL for validation checks

export class TagsService {
    private static readonly CACHE_KEY = 'all_tags';
    private static readonly API_ENDPOINT = '/api/tags';

    static async getTags(): Promise<Tag[]> {
        console.log('🏷️ [TagsService] Getting tags with ETag validation...');

        // Try cache first
        const cachedData = tagCache.get(this.CACHE_KEY);

        try {
            // Always make request with ETag header if we have one
            const headers: HeadersInit = {};
            if (cachedData?.etag) {
                headers['If-None-Match'] = cachedData.etag;
                console.log('🔍 [TagsService] Sending ETag for validation:', cachedData.etag);
            } else {
                console.log('🔄 [TagsService] No cached data, fetching fresh...');
            }

            const response = await fetch(this.API_ENDPOINT, { headers });
            console.log('📡 [TagsService] Response status:', response.status);
            console.log('📡 [TagsService] Response ETag:', response.headers.get('ETag'));

            // 304 means data hasn't changed
            if (response.status === 304) {
                console.log('✅ [TagsService] Data not modified (304), using cached tags:', cachedData?.data?.length || 0);
                return cachedData?.data || [];
            }

            // Data has changed or no cache - parse the response
            console.log('🔄 [TagsService] Fetching fresh data (status:', response.status, ')');

            if (!response.ok) {
                throw new Error(`API responded with status: ${response.status}`);
            }

            const result: ApiResponse = await response.json();
            const newETag = response.headers.get('ETag') || '';

            if (result.success && result.data) {
                console.log('✅ [TagsService] API fetch successful:', result.data.length, 'tags');
                console.log('🏷️ [TagsService] New ETag received:', newETag);

                // Cache the result with ETag
                const cacheData: CachedData = {
                    data: result.data,
                    etag: newETag,
                    timestamp: Date.now()
                };
                tagCache.set(this.CACHE_KEY, cacheData);

                return result.data;
            } else {
                throw new Error(result.error || 'API returned unsuccessful response');
            }
        } catch (error) {
            console.error('⚠️ [TagsService] API fetch failed:', error);

            // If we have cached data, return it as fallback
            if (cachedData?.data) {
                console.log('📦 [TagsService] API failed, returning cached data as fallback');
                return cachedData.data;
            }

            // Fallback to hardcoded tags
            console.log('❌ [TagsService] No data available, using fallback tags');
            return [
                { id: 1, name: 'Genbrug', created_at: new Date().toISOString() },
                { id: 2, name: 'Honey', created_at: new Date().toISOString() },
                { id: 3, name: 'Vegetables', created_at: new Date().toISOString() },
                { id: 7, name: 'Books', created_at: new Date().toISOString() },
                { id: 8, name: 'Clothes', created_at: new Date().toISOString() },
                { id: 9, name: 'Electronics', created_at: new Date().toISOString() },
                { id: 10, name: 'Antiques', created_at: new Date().toISOString() }
            ];
        }
    }

    static clearCache(): void {
        console.log('🗑️ [TagsService] Clearing tag cache...');
        tagCache.clear();
    }
}

// Export convenience functions
export const tagsService = {
    getTags: () => TagsService.getTags(),
    clearCache: () => TagsService.clearCache()
};